import React, { useState, useEffect, useRef } from 'react';
import { Globe } from 'lucide-react';

export const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('EN');
  const [isInitialized, setIsInitialized] = useState(false);
  const initRef = useRef(false);

  const languages = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩' },
    { code: 'zh-CN', label: '中文', flag: '🇨🇳' },
    { code: 'ja', label: '日本語', flag: '🇯🇵' },
    { code: 'ko', label: '한국어', flag: '🇰🇷' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', label: 'Português', flag: '🇵🇹' },
    { code: 'ar', label: 'العربية', flag: '🇸🇦' },
    { code: 'nl', label: 'Nederlands', flag: '🇳🇱' }
  ];

  useEffect(() => {
    // Prevent double initialization
    if (initRef.current) return;
    initRef.current = true;

    // Initialize Google Translate
    const initGoogleTranslate = () => {
      // Define the callback function globally
      window.googleTranslateElementInit = () => {
        if (document.getElementById('google_translate_element')) {
          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'en',
              includedLanguages: 'en,id,zh-CN,ja,ko,fr,de,es,it,pt,ar,nl',
              layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
              autoDisplay: false
            },
            'google_translate_element'
          );
          
          // Wait for Google Translate to fully initialize
          const checkInit = setInterval(() => {
            const select = document.querySelector('.goog-te-combo');
            if (select) {
              setIsInitialized(true);
              clearInterval(checkInit);
            }
          }, 100);
          
          // Stop checking after 10 seconds
          setTimeout(() => clearInterval(checkInit), 10000);
        }
      };

      // Load Google Translate script if not already loaded
      if (!document.querySelector('script[src*="translate.google.com"]')) {
        const script = document.createElement('script');
        script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        document.body.appendChild(script);
      } else if (window.google && window.google.translate) {
        // If script already loaded, just initialize
        window.googleTranslateElementInit();
      }
    };

    initGoogleTranslate();
  }, []);

  const handleLanguageChange = (code, label) => {
    // Wait for Google Translate to be initialized
    const triggerTranslation = () => {
      const googleTranslateSelect = document.querySelector('.goog-te-combo');
      if (googleTranslateSelect) {
        googleTranslateSelect.value = code;
        googleTranslateSelect.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Update display label
        const displayLabel = label.split(' ')[0].toUpperCase().substring(0, 2);
        setCurrentLang(displayLabel);
      }
    };

    if (isInitialized) {
      triggerTranslation();
    } else {
      // If not initialized yet, wait and retry
      let retries = 0;
      const retryInterval = setInterval(() => {
        const select = document.querySelector('.goog-te-combo');
        if (select || retries > 50) {
          clearInterval(retryInterval);
          if (select) {
            triggerTranslation();
            setIsInitialized(true);
          }
        }
        retries++;
      }, 100);
    }
    
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Google Translate element - must be in DOM and visible (but styled hidden) for initialization */}
      <div 
        id="google_translate_element" 
        style={{ 
          position: 'fixed',
          bottom: '-100px',
          left: '0',
          opacity: '0',
          pointerEvents: 'none',
          zIndex: -1
        }} 
      />
      
      {/* Custom visible selector */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 16px',
          background: 'transparent',
          border: '1px solid #FBBF24',
          borderRadius: '4px',
          color: '#FBBF24',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#FBBF24';
          e.currentTarget.style.color = '#070D1E';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = '#FBBF24';
        }}
      >
        <Globe size={16} />
        <span>{currentLang}</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(251, 191, 36, 0.2)',
            borderRadius: '8px',
            minWidth: '200px',
            maxHeight: '400px',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            zIndex: 1000
          }}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code, lang.label)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'transparent',
                border: 'none',
                color: '#E2E8F0',
                fontSize: '14px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(251, 191, 36, 0.1)';
                e.currentTarget.style.color = '#FBBF24';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#E2E8F0';
              }}
            >
              <span style={{ fontSize: '18px' }}>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}

      <style>{`
        /* Hide Google Translate UI elements */
        .goog-te-banner-frame,
        .goog-te-balloon-frame {
          display: none !important;
        }
        
        #google_translate_element .goog-te-gadget {
          display: none !important;
        }
        
        body {
          top: 0 !important;
        }
        
        body > .skiptranslate {
          display: none !important;
        }
        
        iframe.skiptranslate {
          visibility: hidden !important;
          height: 0 !important;
        }
        
        .goog-te-banner-frame.skiptranslate {
          display: none !important;
        }
      `}</style>
    </div>
  );
};
